import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
    Store, ShoppingBag, CalendarDays, Handshake,
    UserCircle, Lightbulb, Users, MessageSquare,
} from 'lucide-react';

/**
 * ZoneSectionNav — flat, single-level navigation for the zone's tabs.
 *
 * All sections (discover + community) live side by side and wrap into
 * two even rows. No horizontal scrolling, no group switch.
 */
const ALL_TABS = [
    // Row 1 — "what's in this place"
    { id: 'businesses', label: 'עסקים',     icon: Store },
    { id: 'stores',     label: 'חנויות',    icon: ShoppingBag },
    { id: 'events',     label: 'מפגשים',    icon: CalendarDays },
    { id: 'barters',    label: 'בארטרים',   icon: Handshake },
    // Row 2 — "who's in this place"
    { id: 'about',      label: 'על הקהילה',  icon: MessageSquare },
    { id: 'members',    label: 'חברי קהילה', icon: UserCircle },
    { id: 'skills',     label: 'כישורים',    icon: Lightbulb },
    { id: 'groups',     label: 'קבוצות',     icon: Users },
];

export function ZoneSectionNav({
    activeTab,
    onTabChange,
    hasAbout,
    counts = {},
    variant = 'panel',
}) {
    const floating = variant === 'floating';
    // Drop About when the zone has no description, otherwise keep order stable.
    const visibleTabs = useMemo(
        () => ALL_TABS.filter(t => t.id !== 'about' || hasAbout),
        [hasAbout],
    );

    // flex-wrap + justify-center: chips auto-size to their label, and any short
    // trailing row (e.g. 4 + 3 when About is hidden) sits centered instead of
    // left-aligned. max-w-3xl keeps the total width tight so chips wrap to 2 rows.
    if (floating) {
        // Glass dock — one horizontal row that floats on the map. Inactive
        // tabs are icon-only beads; the active tab springs open into a brand
        // pill with its label (framer `layout` animates the size reflow,
        // including the rail's own width).
        return (
            <motion.div
                layout
                role="tablist"
                aria-label="קטעי הקהילה"
                transition={{ type: 'spring', stiffness: 440, damping: 36 }}
                className={clsx(
                    'flex items-center gap-1 w-fit max-w-full overflow-x-auto',
                    'p-1.5 rounded-pill',
                    'bg-white/65 backdrop-blur-xl backdrop-saturate-150',
                    'ring-1 ring-white/70 shadow-elevated',
                    '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
                )}
            >
                {visibleTabs.map(({ id, label, icon: Icon }) => {
                    const active = activeTab === id;
                    const count = counts[id];
                    return (
                        <motion.button
                            key={id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            aria-label={label}
                            title={label}
                            onClick={() => onTabChange(id)}
                            layout
                            whileTap={{ scale: 0.92 }}
                            transition={{ type: 'spring', stiffness: 440, damping: 36 }}
                            className={clsx(
                                'relative inline-flex items-center justify-center shrink-0 h-10 rounded-pill',
                                'transition-colors duration-200 ease-smooth',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
                                active
                                    ? 'gap-1.5 ps-3.5 pe-3 bg-brand-500 text-ink-inverse shadow-card'
                                    : 'w-10 text-ink-muted hover:text-ink hover:bg-black/[0.06]',
                            )}
                        >
                            <Icon size={18} strokeWidth={2.4} className="shrink-0" />
                            {active && (
                                <motion.span
                                    layout="position"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.18, delay: 0.04 }}
                                    className="flex items-center gap-1.5 whitespace-nowrap text-sm font-bold"
                                >
                                    <span>{label}</span>
                                    {typeof count === 'number' && count > 0 && (
                                        <span className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-pill bg-white/25 text-2xs font-bold">
                                            {count}
                                        </span>
                                    )}
                                </motion.span>
                            )}
                        </motion.button>
                    );
                })}
            </motion.div>
        );
    }

    return (
        <div
            className="sticky top-0 z-30"
            style={{ background: 'rgba(255, 255, 255, 0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
        >
            <div className="px-3 sm:px-6 pt-3 pb-3 border-b border-line-subtle/70">
                <div
                    role="tablist"
                    aria-label="קטעי הקהילה"
                    className="flex flex-wrap justify-center gap-1.5 sm:gap-2 max-w-3xl mx-auto"
                >
                    {visibleTabs.map(({ id, label, icon: Icon }, idx) => {
                        const active = activeTab === id;
                        const count = counts[id];
                        return (
                            <motion.button
                                key={id}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => onTabChange(id)}
                                whileTap={{ scale: 0.97 }}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.22, delay: idx * 0.02 }}
                                className={clsx(
                                    'inline-flex items-center justify-center gap-1.5 h-9 sm:h-10 px-3 sm:px-4 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap',
                                    'transition-[background-color,color,box-shadow] duration-200 ease-smooth',
                                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                                    active
                                        ? 'bg-brand-500 text-ink-inverse shadow-card'
                                        : 'text-ink-muted hover:text-ink hover:bg-surface-muted',
                                )}
                            >
                                <Icon size={14} strokeWidth={active ? 2.5 : 2.25} className="shrink-0" />
                                <span>{label}</span>
                                {typeof count === 'number' && count > 0 && (
                                    <span
                                        className={clsx(
                                            'hidden sm:inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-md text-2xs font-bold shrink-0',
                                            active
                                                ? 'bg-white/20 text-ink-inverse'
                                                : 'bg-surface-muted text-ink-faint',
                                        )}
                                    >
                                        {count}
                                    </span>
                                )}
                            </motion.button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default ZoneSectionNav;
